import useBrainSearchBot from "@/admin/integrations/chat-bot/util/useBrainSearchBot"
import BookmarkButton from "@/bookmarks/BookmarkButton"
import BrainSearchMoreActionsButton from "@/brain-search/internal/BrainSearchMoreActionsButton"
import { BrainSearchPageHeaderFragment$key } from "@/brain-search/internal/__generated__/BrainSearchPageHeaderFragment.graphql"
import BrainSearchSidePanelToggle from "@/brain-search/internal/drawer/BrainSearchSidePanelToggle"
import { ContentUsageUtils } from "@/content-usage/ContentUsageUtils"
import { useContentUsageDrawerContext } from "@/content-usage/drawer/ContentUsageDrawerContext"
import ContentUtils from "@/content/util/contentUtils"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import ROUTE_NAMES from "@/core/route/util/routeNames"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import styleIf from "@assets/style/util/styleIf"
import { DiscoIcon, DiscoIconButton, DiscoLink, DiscoText } from "@disco-ui"
import { useIsMobile } from "@utils/hook/screenSizeHooks"
import { TestIDProps } from "@utils/typeUtils"
import { Fragment } from "react"
import { graphql, useFragment } from "react-relay"
import { generatePath } from "react-router-dom"

interface Props extends TestIDProps {
  brainSearchKey?: BrainSearchPageHeaderFragment$key
}

function BrainSearchPageHeader({
  testid = "BrainSearchPageHeader",
  brainSearchKey,
}: Props) {
  const drawer = useGlobalDrawer("brainSearch")
  const contentUsageDrawerContext = useContentUsageDrawerContext()
  const isSidePanel = Boolean(drawer.params.drawerAsk)
  const classes = useStyles({
    isSidePanel,
    // Match the content usage drawer's header height if it's open
    headerHeight: contentUsageDrawerContext.headerHeight,
  })
  const isMobile = useIsMobile()
  const bot = useBrainSearchBot()!

  const brainSearch = useFragment<BrainSearchPageHeaderFragment$key>(
    graphql`
      fragment BrainSearchPageHeaderFragment on BrainSearch {
        id
        title
        contentUsage {
          id
          content {
            type
            name
          }
          product {
            name
            slug
          }
          parentApp {
            kind
            customAppTitle
          }
          ...ContentUsageUtils_useContentUsagePathContentUsageFragment
          ...BrainSearchSidePanelToggle_ContentUsageFragment
        }
        ...BrainSearchMoreActionsButtonFragment
        ...BookmarkButtonBrainSearchFragment
      }
    `,
    brainSearchKey || null
  )
  const contentLabel = ContentUtils.useDefaultContentLabel(
    brainSearch?.contentUsage?.content?.type
  )
  const contentUsagePath = ContentUsageUtils.useContentUsagePath(
    brainSearch?.contentUsage
  )

  const isWide = !isMobile && !isSidePanel

  return (
    <div className={classes.header}>
      <div className={classes.lhsHeader}>
        {isWide && brainSearch ? (
          <div className={classes.titleContainer}>
            <DiscoText variant={"body-md-600"} truncateText={1}>
              {brainSearch.title}
            </DiscoText>
            {brainSearch.contentUsage && (
              <DiscoText
                variant={"body-xs-500"}
                color={"text.secondary"}
                truncateText={1}
              >
                {"in "}
                {renderParentLinks()}
                {`${contentLabel}: `}
                <DiscoLink
                  textVariant={"body-xs-500"}
                  to={{
                    pathname: contentUsagePath,
                    search: `u=${Relay.fromGlobalId(brainSearch.contentUsage.id).id}`,
                  }}
                >
                  {brainSearch.contentUsage.content.name}
                </DiscoLink>
              </DiscoText>
            )}
          </div>
        ) : (
          <>
            <div className={classes.iconContainer}>
              <DiscoIcon icon={"stars"} active />
            </div>
            <DiscoText variant={"body-lg-600"}>{bot.name}</DiscoText>
          </>
        )}
      </div>

      <div className={classes.rhsHeader}>
        {!isMobile && (
          <BrainSearchSidePanelToggle contentUsageKey={brainSearch?.contentUsage} />
        )}
        {brainSearch && (
          <>
            <BrainSearchMoreActionsButton brainSearchKey={brainSearch} />
            <BookmarkButton kind={"brain_search"} brainSearchKey={brainSearch} />
          </>
        )}
        <DiscoIconButton onClick={drawer.close} testid={`${testid}.close-button`}>
          <DiscoIcon icon={"close"} />
        </DiscoIconButton>
      </div>
    </div>
  )

  function renderParentLinks() {
    const { product, parentApp } = brainSearch!.contentUsage!
    const links: { name: string; path: string }[] = []
    if (product) {
      links.push({
        name: product.name,
        path: generatePath(ROUTE_NAMES.PRODUCT.ROOT, {
          productSlug: product.slug,
        }),
      })
    }
    if (parentApp?.kind === "collection") {
      links.push({
        name: parentApp.customAppTitle || "Collection",
        path: contentUsagePath,
      })
    }
    return links.map(({ name, path }) => (
      <Fragment key={path}>
        <DiscoLink textVariant={"body-xs-500"} to={path}>
          {name}
        </DiscoLink>
        {` • `}
      </Fragment>
    ))
  }
}

type StyleProps = {
  isSidePanel: boolean
  headerHeight: number | undefined
}

const useStyles = makeUseStyles((theme) => ({
  header: ({ isSidePanel, headerHeight }: StyleProps) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    minWidth: 0,
    gap: theme.spacing(2),
    position: "sticky",
    top: 0,
    zIndex: theme.zIndex.raise1,
    padding: theme.spacing(2, 2, 2, 3),
    backgroundColor: theme.palette.background.paper,
    height: theme.mixins.toolbar.minHeight,
    flexShrink: 0,
    borderBottom: theme.palette.constants.borderSmall,
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(2),
    },
    ...styleIf(isSidePanel, {
      paddingLeft: theme.spacing(2),
      height: headerHeight,
    }),
  }),
  lhsHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  iconContainer: {
    display: "flex",
    padding: theme.spacing(1),
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.groovy.onDark[400]
        : theme.palette.groovy.neutral[200],
    borderRadius: theme.measure.borderRadius.medium,
  },
  rhsHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
}))

export default BrainSearchPageHeader
